import SparklesIcon from "@entrilia/shared/icons/SparklesIcon";
import StopIcon from "@mui/icons-material/StopCircleOutlined";
import type { Theme } from "@mui/material";
import { Alert, Box, Button, Stack, Typography } from "@mui/material";
import type { SystemStyleObject } from "@mui/system";
import type { DragEvent } from "react";
import type {
  AssistantAction,
  AssistantAttachment,
  AssistantContentRef,
  AssistantFile,
  AssistantMappingChoices,
} from "../../../api/types/assistantTypes";
import useUserContext from "../../../context/useUserContext";
import InputPrompt from "./InputPrompt";
import ChatHistory from "./chat/ChatHistory";
import type { ChatItem } from "./chatState";
import StartScreenTasks from "./tasks/StartScreenTasks";

interface Props {
  items: ChatItem[];
  promptDisabled: boolean;
  isReadOnly: boolean;
  stopDisabled: boolean;
  openContent?: AssistantContentRef;
  onUploadFile: (file: File, signal: AbortSignal) => Promise<AssistantAttachment>;
  onSubmit: (prompt: string, attachments: AssistantAttachment[]) => Promise<boolean>;
  onStopGenerating: () => void;
  onActionDecision: (action: AssistantAction, approve: boolean, mappingChoices?: AssistantMappingChoices) => void;
  onOpenFile: (file: AssistantFile) => void;
  onToggleContent: (content: AssistantContentRef) => void;
}

// Names renamed in Operations are stored as "Last, First".
const getFirstName = (name: string): string | undefined => {
  const [beforeComma, afterComma] = name.split(",");
  const [firstName] = (afterComma ?? beforeComma ?? "").trim().split(" ");
  return firstName || undefined;
};

const chatContentSx: SystemStyleObject<Theme> = { width: "100%", maxWidth: "lg", px: 7 };

const preventFileDropNavigation = (event: DragEvent<HTMLDivElement>) => {
  if (event.dataTransfer.types.includes("Files")) {
    event.preventDefault();
  }
};

const Chat = ({
  items,
  promptDisabled,
  isReadOnly,
  stopDisabled,
  openContent,
  onUploadFile,
  onSubmit,
  onStopGenerating,
  onActionDecision,
  onOpenFile,
  onToggleContent,
}: Props) => {
  const { name } = useUserContext();

  const isEmpty = items.length === 0;
  const showStopGenerationButton = items.some((item) => item.isPending);
  const firstName = getFirstName(name);

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        flex: 1,
        minHeight: 0,
        bgcolor: "background.grey",
        overflowY: isEmpty ? "auto" : undefined,
      }}
      onDragOver={preventFileDropNavigation}
      onDrop={preventFileDropNavigation}
    >
      {isEmpty ? (
        <Stack spacing={2} sx={{ alignItems: "center", pt: 10, pb: 6 }}>
          <SparklesIcon color="primary" fontSize="large" />
          <Typography variant="h3">{firstName ? `Hi ${firstName}` : "Hi"}, what would you like to do?</Typography>
        </Stack>
      ) : (
        <ChatHistory
          items={items}
          actionsDisabled={promptDisabled}
          contentSx={chatContentSx}
          openContent={openContent}
          onActionDecision={onActionDecision}
          onOpenFile={onOpenFile}
          onReply={(reply) => onSubmit(reply, [])}
          onToggleContent={onToggleContent}
        />
      )}

      <Stack spacing={isEmpty ? 6 : 1} useFlexGap sx={[chatContentSx, { alignItems: "center", pb: 3 }]}>
        {showStopGenerationButton && (
          <Button
            variant="outlined"
            color="secondary"
            sx={{ py: "2px", my: 1 }}
            startIcon={<StopIcon />}
            disabled={stopDisabled}
            onClick={onStopGenerating}
          >
            Stop generating
          </Button>
        )}

        {isReadOnly && (
          <Alert severity="warning" sx={{ width: "100%" }}>
            Your access has changed, so this chat can't continue. Start a new chat.
          </Alert>
        )}

        <InputPrompt disabled={promptDisabled} readOnly={isReadOnly} onUploadFile={onUploadFile} onSubmit={onSubmit} />

        {isEmpty && <StartScreenTasks />}
      </Stack>
    </Box>
  );
};

export default Chat;
